import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spin, Row, Col, Table, Statistic, Button, message, Select, Space, InputNumber, Modal, DatePicker, Collapse, Upload } from 'antd';
import { DownloadOutlined, ReloadOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { yuan, percent } from '../utils/format.js';
import { apiFetch, apiDownload, apiUpload, onDataChanged } from '../utils/api.js';

const { RangePicker } = DatePicker;

const YEAR_OPTIONS = ['2026', '2025', '2024'];
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}月` }));

// 每日推广费：单店视图行内点击录入（留空=删除）；全部店铺视图点击弹出「选店铺+金额」窗口
function AdSpendCell({ date, amount, shop, onSave, onAddAll }) {
  const editable = shop !== 'all';
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(amount || '');
  const save = async () => {
    setEditing(false);
    const v = (val === '' || val == null) ? '' : Number(val);
    await onSave(date, v, shop); // 传当前单元格所属店铺（店铺明细=该店，主表=全局筛选店）
  };
  if (editing) {
    return (
      <InputNumber size="small" min={0} precision={2} value={val} autoFocus
        onChange={setVal} onBlur={save} onPressEnter={save} placeholder="留空=删除" style={{ width: 84 }} />
    );
  }
  if (!editable) {
    return (
      <span style={{ cursor: 'pointer', userSelect: 'none', color: amount ? '#EF9F27' : '#8c8c8c' }}
        title="点击录入推广费（选择店铺与金额）"
        onClick={() => onAddAll(date)}>
        {amount ? `¥${yuan(amount)}` : '录入'}<EditOutlined style={{ fontSize: 10, color: '#bfbfbf', marginLeft: 3 }} />
      </span>
    );
  }
  return (
    <span style={{ cursor: 'pointer', userSelect: 'none', color: amount ? '#EF9F27' : '#8c8c8c' }}
      title="点击录入/修改该店铺当日推广费，留空删除"
      onClick={() => { setVal(amount || ''); setEditing(true); }}>
      {amount ? `¥${yuan(amount)}` : '录入'}<EditOutlined style={{ fontSize: 10, color: '#bfbfbf', marginLeft: 3 }} />
    </span>
  );
}

export default function FinanceAnalysis() {
  const [data, setData] = useState(null);
  const [allYears, setAllYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [range, setRange] = useState(null); // [dayjs, dayjs] 或 null（null=全部）
  const [shop, setShop] = useState('all');
  const [channel, setChannel] = useState('all'); // 渠道筛选：all/pdd/wechat/taobao
  const [shops, setShops] = useState([]);
  const [adModal, setAdModal] = useState(null); // {date, values} 全部店铺视图录入推广费弹窗
  const [detailShop, setDetailShop] = useState(''); // 店铺每日明细当前查看的店铺
  const [detailData, setDetailData] = useState(null); // 该店财务数据 {daily, totals}
  const [importingAd, setImportingAd] = useState(false); // 推广费导入中
  const [prodPaidMap, setProdPaidMap] = useState({}); // {spec_code: {ad_spend, ratio, revenue}} 产品级推广费
  const [prodAdModal, setProdAdModal] = useState(null); // {spec_code, product_name, date, amount} 产品推广费录入弹窗
  const [exportModal, setExportModal] = useState(false); // 导出报表弹窗
  const [exportYear, setExportYear] = useState(String(dayjs().year()));   // 导出年份
  const [exportMonth, setExportMonth] = useState(String(dayjs().month() + 1)); // 导出月份（空=全部）

  // —— 两个面板的独立日期范围筛选（默认当前自然月整月） ——
  const nowMonthRange = [dayjs().startOf('month'), dayjs().endOf('month')];
  const [detailRange, setDetailRange] = useState(nowMonthRange); // 店铺每日明细：日期范围
  const [dailyRange, setDailyRange] = useState(nowMonthRange);   // 每日财务明细：日期范围
  const [dailyData, setDailyData] = useState(null);              // 每日财务明细面板的独立数据 {daily, totals}

  const loadData = useCallback((r, s, silent = false, c) => {
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    if (r && r[0] && r[1]) {
      params.set('start', r[0].format('YYYY-MM-DD'));
      params.set('end', r[1].format('YYYY-MM-DD'));
    }
    if (s && s !== 'all') params.set('shop', s);
    const ch = c === undefined ? channel : c;
    if (ch && ch !== 'all') params.set('channel', ch);
    apiFetch(`/api/analysis/finance${params.toString() ? '?' + params.toString() : ''}`)
      .then(resp => resp.json())
      .then(dd => {
        setData(dd);
        // 从全量数据提取可用年份（仅首次）
        if (dd.daily && dd.daily.length) {
          const ys = [...new Set(dd.daily.map(x => String(x.shipping_date).slice(0, 4)))].filter(Boolean).sort().reverse();
          if (ys.length) setAllYears(ys);
        }
        if (!silent) setLoading(false);
      })
      .catch(() => { if (!silent) setLoading(false); });
  }, [channel]);

  useEffect(() => { loadData(range, shop); }, [loadData]); // channel 变化时用当前 range/shop 重载（避免丢失已选筛选）
  // 店铺列表（财务页店铺筛选用）；默认显示第一个店铺的每日明细
  useEffect(() => { apiFetch('/api/filters').then(r => r.json()).then(f => { setShops(f.shops || []); setDetailShop(s => s || (f.shops && f.shops[0]) || ''); }).catch(() => {}); }, []);

  // 店铺每日明细：加载指定店铺的财务数据（使用独立年月日筛选）
  const loadDetail = useCallback((s) => {
    if (!s) return;
    const params = new URLSearchParams();
    if (detailRange && detailRange[0] && detailRange[1]) {
      params.set('start', detailRange[0].format('YYYY-MM-DD'));
      params.set('end', detailRange[1].format('YYYY-MM-DD'));
    }
    params.set('shop', s);
    apiFetch(`/api/analysis/finance?${params}`)
      .then(r => r.json())
      .then(d => setDetailData(d))
      .catch(() => setDetailData(null));
  }, [detailRange]);

  // 店铺每日明细：切换店铺/日期筛选时加载该店数据
  useEffect(() => { loadDetail(detailShop); }, [detailShop, loadDetail]);

  // 每日财务明细面板：使用独立年月日筛选（店铺沿用全局 shop）加载一份独立数据
  const loadDailyTable = useCallback(() => {
    const params = new URLSearchParams();
    if (dailyRange && dailyRange[0] && dailyRange[1]) {
      params.set('start', dailyRange[0].format('YYYY-MM-DD'));
      params.set('end', dailyRange[1].format('YYYY-MM-DD'));
    }
    if (shop && shop !== 'all') params.set('shop', shop);
    if (channel && channel !== 'all') params.set('channel', channel);
    apiFetch(`/api/analysis/finance${params.toString() ? '?' + params.toString() : ''}`)
      .then(r => r.json())
      .then(d => setDailyData(d))
      .catch(() => setDailyData(null));
  }, [dailyRange, shop, channel]);
  useEffect(() => { loadDailyTable(); }, [loadDailyTable]);

  // 产品级推广费（按筛选 年/月/日 + 店铺）
  const loadProductPaid = useCallback(() => {
    const params = new URLSearchParams();
    if (range && range[0] && range[1]) {
      params.set('start', range[0].format('YYYY-MM-DD'));
      params.set('end', range[1].format('YYYY-MM-DD'));
    }
    if (shop && shop !== 'all') params.set('shop', shop);
    apiFetch(`/api/finance/product-paid${params.toString() ? '?' + params.toString() : ''}`)
      .then(r => r.json())
      .then(d => {
        const map = {};
        for (const p of d.data || []) map[p.spec_code] = p;
        setProdPaidMap(map);
      })
      .catch(() => setProdPaidMap({}));
  }, [range, shop]);
  useEffect(() => { loadProductPaid(); }, [loadProductPaid]);

  // 保存产品级推广费（date+店铺+spec_code+金额；''=删除）→ 刷新
  const saveProdAd = async () => {
    const m = prodAdModal;
    if (!m || !m.date) { message.warning('请选择日期'); return; }
    try {
      const res = await apiFetch('/api/finance/ad-spend-product', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: m.date, shop: shop, spec_code: m.spec_code, amount: m.amount === undefined ? '' : m.amount }),
      }).then(r => r.json());
      if (res.success) {
        message.success(m.amount === undefined || m.amount === '' ? `已删除推广费` : `已保存推广费 ¥${yuan(m.amount)}`);
        setProdAdModal(null);
        loadProductPaid();
      } else message.error(res.error || '保存失败');
    } catch (e) { message.error('保存失败'); }
  };
  // 数据变更（其它页面改编码/导入等）→ 静默刷新主表 + 店铺明细 + 每日明细面板
  useEffect(() => onDataChanged(() => { loadData(range, shop); loadDetail(detailShop); loadDailyTable(); }), [loadData, loadDetail, loadDailyTable, range, shop, detailShop]);

  const applyFilter = (r, s = shop, c = channel) => {
    setRange(r); setShop(s); setChannel(c);
    loadData(r, s, false, c);
  };

  // 快捷日期范围：本月 / 上月 / 近30天 / 全部
  const quickRange = (key) => {
    if (key === 'month') applyFilter([dayjs().startOf('month'), dayjs().endOf('month')]);
    else if (key === 'lastMonth') applyFilter([dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]);
    else if (key === '30d') applyFilter([dayjs().subtract(29, 'day'), dayjs()]);
    else applyFilter(null);
  };

  // 一键导入推广费（拼多多推广流水明细 .xls/.xlsx）：上传 → 后端解析匹配店铺+日期 → 批量写入
  const handleAdImport = async (file) => {
    setImportingAd(true);
    try {
      const res = await apiUpload('/api/ad-spend/import', file);
      if (res.success) {
        const warn = (res.unmatched_shops && res.unmatched_shops.length)
          ? `；${res.unmatched_shops.length} 家店铺未匹配（${res.unmatched_shops.join('、')}）`
          : '';
        message.success(`导入成功：${res.imported} 条推广费，合计 ¥${yuan(res.total_amount || 0)}${warn}`, 6);
        loadDetail(detailShop);   // 店铺每日明细同步刷新
        loadDailyTable();         // 每日财务明细面板同步刷新
        loadData(range, shop, true); // 主表静默刷新
      } else {
        message.error(res.error || '导入失败');
      }
    } catch (e) {
      message.error(e.message || '导入失败');
    } finally {
      setImportingAd(false);
    }
    return false; // 阻止 Upload 默认自动上传
  };

  // 保存某店某日推广费（''=删除）→ 刷新主表 + 若影响当前店铺则刷新店铺明细
  const saveAdSpend = async (date, amount, adShop = shop) => {
    const res = await apiFetch('/api/ad-spend', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, shop: adShop, amount }),
    }).then(r => r.json());
    if (res.success) {
      message.success(amount === '' ? `已删除 ${date} 的推广费` : `已保存 ${date} 推广费 ¥${yuan(amount)}`);
      loadData(range, shop, true); // 静默刷新（无 loading 闪烁）
      loadDetail(detailShop); // 店铺每日明细同步刷新（录入的就是当前店铺）
      loadDailyTable(); // 每日财务明细面板同步刷新
    } else message.error(res.error || '保存失败');
    return res;
  };
  // 打开录入弹窗：列所有店铺、预填该日各店已有推广费
  const openAdModal = async (date) => {
    const values = {};
    try {
      const res = await apiFetch('/api/ad-spend').then(r => r.json());
      for (const it of (res.data || [])) if (it.date === date) values[it.shop] = it.amount;
    } catch { /* ignore */ }
    setAdModal({ date, values });
  };
  // 一次保存多店：每店金额（空=删除该店该日记录）
  const saveAdModal = async () => {
    const date = adModal?.date;
    const values = adModal?.values || {};
    for (const s of shops) {
      const v = values[s];
      const amount = (v === '' || v == null || v === undefined) ? '' : Number(v);
      await apiFetch('/api/ad-spend', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, shop: s, amount }),
      }).then(r => r.json());
    }
    message.success(`已保存 ${date} 推广费`);
    setAdModal(null);
    loadData(range, shop, true); // 静默刷新
    loadDetail(detailShop); // 弹窗可能改了当前店铺，同步刷新店铺明细
    loadDailyTable(); // 每日财务明细面板同步刷新
  };

  const doExport = async () => {
    if (!exportYear) { message.warning('请选择年份'); return; }
    setExporting(true);
    try {
      const m = exportMonth ? `${exportYear}-${String(exportMonth).padStart(2, '0')}` : '';
      const url = '/api/export/report' + (m ? `?month=${m}` : '');
      await apiDownload(url, '电商管理工作台_报表.xlsx');
      message.success('报表已导出');
      setExportModal(false);
    } catch (e) {
      message.error(e.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  if (loading || !data) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  const { daily, productFinance, totals, regions = [] } = data;
  const totalRevenue = totals?.revenue ?? daily.reduce((a, d) => a + d.revenue, 0);
  const totalProfit = totals?.profit ?? daily.reduce((a, d) => a + d.profit, 0);
  const actualProfit = totals?.actual_profit ?? daily.reduce((a, d) => a + (d.actual_profit || 0), 0);
  const afterSaleImpact = totals?.after_sale_impact ?? daily.reduce((a, d) => a + (d.after_sale_impact || 0), 0);
  const adSpendTotal = totals?.ad_spend ?? daily.reduce((a, d) => a + (d.ad_spend || 0), 0);
  const totalCost = daily.reduce((a, d) => a + d.cost, 0);
  const totalShipping = daily.reduce((a, d) => a + d.shipping, 0);
  const totalPackaging = daily.reduce((a, d) => a + d.packaging, 0);
  const totalPlatform = daily.reduce((a, d) => a + d.platform_fee, 0);
  const totalOrders = totals?.order_count ?? daily.reduce((a, d) => a + (d.order_count || 0), 0);
  const rangeLabel = totals?.startDate ? `${totals.startDate} ~ ${totals.endDate}` : '全部时间';
  const yearOptions = (allYears.length ? allYears : YEAR_OPTIONS).map(y => ({ value: y, label: `${y}年` }));

  // 区域销售占比饼图（一区/二区/三区/四区 订单数占比）
  const regionColorOf = (r) => ({ '一区': '#D85A30', '二区': '#378ADD', '三区': '#1D9E75', '四区': '#EF9F27' }[r] || '#8c8c8c');
  const regionPieOption = {
    tooltip: {
      trigger: 'item',
      formatter: p => {
        const r = regions.find(x => x.region_view === p.name);
        const rev = r ? `¥${yuan(r.revenue)}` : '0';
        return `${p.name}<br/>订单：${p.value} 单（${p.percent}%）<br/>营收：${rev}`;
      },
    },
    legend: { bottom: 0 },
    series: [{
      type: 'pie',
      radius: ['38%', '66%'],
      center: ['50%', '44%'],
      label: { formatter: '{b}\n{c} 单 · {d}%' },
      data: regions.map(r => ({
        name: r.region_view,
        value: r.order_count,
        itemStyle: { color: regionColorOf(r.region_view) },
      })),
    }],
  };

  // 利润与实际利润（逐单按售后类型重算后）
  const actualProfitOption = {
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        const d = daily[params[0].dataIndex];
        return `${d.shipping_date}<br/>订单利润: ¥${yuan(d.profit)}<br/>推广费: ¥${yuan(d.ad_spend || 0)}<br/>实际利润: ¥${yuan(d.actual_profit)}<br/>售后影响: -¥${yuan(d.after_sale_impact || 0)}`;
      },
    },
    legend: { data: ['订单利润', '实际利润'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: daily.map(d => d.shipping_date.slice(5)), boundaryGap: false },
    yAxis: { type: 'value', name: '元' },
    series: [
      { name: '订单利润', type: 'line', smooth: true, itemStyle: { color: '#EF9F27' }, data: daily.map(d => d.profit) },
      { name: '实际利润', type: 'line', smooth: true, itemStyle: { color: '#D4537E' }, data: daily.map(d => d.actual_profit) },
    ],
  };

  // 商品利润排行
  const top10Profit = productFinance.slice(0, 10);
  const profitBarOption = {
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      formatter: params => {
        const p = top10Profit[params[0].dataIndex];
        return `${p.product_name}<br/>营收: ¥${yuan(p.revenue)}<br/>成本: ¥${yuan(p.cost)}<br/>利润: ¥${yuan(p.profit)}<br/>毛利率: ${percent(p.avg_margin, 3)}%`;
      },
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'value', name: '利润(元)' },
    yAxis: { type: 'category', data: top10Profit.map(p => p.product_name).reverse(), axisLabel: { fontSize: 11 } },
    series: [{
      type: 'bar',
      data: top10Profit.map(p => ({ value: p.profit, itemStyle: { color: '#CF1322' } })).reverse(),
    }],
  };

  // 成本占比饼图
  const costPieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
    legend: { bottom: 0 },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      label: { formatter: '{b}\n{d}%' },
      data: [
        { name: '商品成本', value: +totalCost.toFixed(2), itemStyle: { color: '#8C8178' } },
        { name: '运费', value: +totalShipping.toFixed(2), itemStyle: { color: '#EF9F27' } },
        { name: '辅材成本', value: +totalPackaging.toFixed(2), itemStyle: { color: '#378ADD' } },
        { name: '技术服务费', value: +totalPlatform.toFixed(2), itemStyle: { color: '#7F77DD' } },
        { name: '利润', value: +totalProfit.toFixed(2), itemStyle: { color: '#CF1322' } },
      ],
    }],
  };

  const productColumns = [
    { title: '排名', width: 60, render: (_, __, idx) => idx + 1 },
    { title: '商品名称', dataIndex: 'product_name', width: 180 },
    { title: '规格编码', dataIndex: 'spec_code', width: 150 },
    { title: '订单数', dataIndex: 'order_count', width: 70, align: 'right', sorter: (a, b) => a.order_count - b.order_count },
    { title: '出货量', dataIndex: 'quantity', width: 90, align: 'right', render: v => v.toLocaleString(), sorter: (a, b) => a.quantity - b.quantity },
    { title: '营收', dataIndex: 'revenue', width: 100, align: 'right', sorter: (a, b) => a.revenue - b.revenue, render: v => <span style={{ color: '#D85A30' }}>¥{yuan(v)}</span> },
    { title: '商品成本', dataIndex: 'cost', width: 100, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '运费', dataIndex: 'shipping', width: 80, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '辅材', dataIndex: 'packaging', width: 70, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '技术服务费', dataIndex: 'platform_fee', width: 90, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '利润', dataIndex: 'profit', width: 100, align: 'right',
      sorter: (a, b) => a.profit - b.profit, defaultSortOrder: 'descend',
      render: v => <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(v)}</span> },
    { title: '毛利率', dataIndex: 'avg_margin', width: 90, align: 'right',
      sorter: (a, b) => a.avg_margin - b.avg_margin,
      render: v => {
        const pct = percent(v, 3);
        const color = v >= 0.15 ? '#CF1322' : v >= 0.08 ? '#EF9F27' : '#3F8600';
        return <span style={{ color }}>{pct}%</span>;
      } },
    { title: '推广费', width: 110, align: 'right',
      render: (_, r) => {
        const p = prodPaidMap[r.spec_code];
        const v = p ? p.ad_spend : 0;
        return (
          <Button type="link" size="small" style={{ padding: 0 }}
            onClick={() => setProdAdModal({ spec_code: r.spec_code, product_name: r.product_name, date: '', amount: undefined })}>
            {v > 0 ? `¥${yuan(v)}` : <span style={{ color: '#8c8c8c' }}>录入</span>}
          </Button>
        );
      } },
    { title: '付费占比', width: 90, align: 'right',
      render: (_, r) => {
        const p = prodPaidMap[r.spec_code];
        if (!p || !(p.revenue > 0)) return <span style={{ color: '#bfbfbf' }}>-</span>;
        const color = p.ratio >= 20 ? '#CF1322' : p.ratio >= 10 ? '#EF9F27' : '#3F8600';
        return <span style={{ color, fontWeight: 600 }}>{p.ratio}%</span>;
      } },
  ];

  const dailyColumns = (shopCtx = shop) => [
    { title: '日期', dataIndex: 'shipping_date', width: 110 },
    { title: '订单数', dataIndex: 'order_count', width: 70, align: 'right' },
    { title: '实际营收', dataIndex: 'revenue', width: 100, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '商品成本', dataIndex: 'cost', width: 100, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '运费', dataIndex: 'shipping', width: 80, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '辅材', dataIndex: 'packaging', width: 70, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '订单利润', dataIndex: 'profit', width: 100, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '技术服务费', dataIndex: 'platform_fee', width: 100, align: 'right',
      render: v => <span style={{ color: '#7F77DD' }}>¥{yuan(v)}</span> },
    { title: '推广费', dataIndex: 'ad_spend', width: 100, align: 'right',
      render: (v, r) => <AdSpendCell date={r.shipping_date} amount={v} shop={shopCtx} onSave={saveAdSpend}
        onAddAll={openAdModal} /> },
    { title: '售后影响', dataIndex: 'after_sale_impact', width: 100, align: 'right',
      render: v => (v ? <span style={{ color: '#EF9F27' }}>-¥{yuan(v)}</span> : '-') },
    { title: '实际利润', dataIndex: 'actual_profit', width: 105, align: 'right',
      render: v => <span style={{ color: '#D4537E', fontWeight: 600 }}>¥{yuan(v)}</span> },
  ];

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>财务分析</div>
          <div style={{ color: '#A99F94', fontSize: 11, marginTop: 1 }}>
            实际利润 = 订单利润 − 售后影响 − 技术服务费 − 每日推广费；技术服务费 = 订单实收价 × 0.7% 自动计算（微信订单不参与、整单退回不收费）；退款（拦截/签收/部分）已按售后口径计入「售后影响」中扣除
          </div>
          <div style={{ color: '#A99F94', fontSize: 11, marginTop: 1 }}>
            说明：退款总额是退给消费者的金额，售后影响是利润净损失（已扣回省下的成本/辅材、加回追回款项），两者口径不同，退款已包含在售后影响里扣减
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => applyFilter('', '', '')}>重置筛选</Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => setExportModal(true)}>导出报表</Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 12 }} styles={{ body: { padding: '8px 14px' } }}>
        <Space wrap size="small">
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>日期范围：</span>
          <RangePicker size="small" value={range} onChange={r => applyFilter(r && r[0] && r[1] ? r : null)} allowClear />
          <Space size={4}>
            <Button size="small" onClick={() => quickRange('month')}>本月</Button>
            <Button size="small" onClick={() => quickRange('lastMonth')}>上月</Button>
            <Button size="small" onClick={() => quickRange('30d')}>近30天</Button>
            <Button size="small" onClick={() => quickRange('all')}>全部</Button>
          </Space>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>店铺：</span>
          <Select size="small" style={{ width: 170 }} value={shop} onChange={v => applyFilter(range, v)}
            options={[{ value: 'all', label: '全部店铺' }, ...shops.map(s => ({ value: s, label: s }))]} />
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>渠道：</span>
          <Select size="small" style={{ width: 120 }} value={channel} onChange={v => applyFilter(range, shop, v)}
            options={[{ value: 'all', label: '全部渠道' }, { value: 'pdd', label: '拼多多' }, { value: 'wechat', label: '微信' }, { value: 'taobao', label: '淘宝' }]} />
          <span style={{ color: '#A99F94', fontSize: 11 }}>当前范围：{rangeLabel}</span>
        </Space>
      </Card>

      <div className="fa-kpi-grid">
        <div className="stat-card" style={{ '--sc': '#D85A30' }}>
          <div className="stat-label">总营收</div>
          <div className="stat-value" style={{ color: '#D85A30' }}>¥{yuan(totalRevenue)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#CF1322' }}>
          <div className="stat-label">订单利润</div>
          <div className="stat-value" style={{ color: '#CF1322' }}>¥{yuan(totalProfit)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#EF9F27' }}>
          <div className="stat-label">推广费</div>
          <div className="stat-value" style={{ color: '#EF9F27' }}>¥{yuan(adSpendTotal)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#8B5CF6' }}>
          <div className="stat-label">技术服务费</div>
          <div className="stat-value" style={{ color: '#8B5CF6' }}>¥{yuan(totalPlatform)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#993C1D' }}>
          <div className="stat-label">实际利润</div>
          <div className="stat-value" style={{ color: '#993C1D' }}>¥{yuan(actualProfit)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#7F77DD' }}>
          <div className="stat-label">售后影响</div>
          <div className="stat-value" style={{ color: '#7F77DD' }}>-¥{yuan(afterSaleImpact)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#1890FF' }}>
          <div className="stat-label">订单数</div>
          <div className="stat-value" style={{ color: '#1890FF' }}>{totalOrders.toLocaleString()}<span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 2 }}>单</span></div>
        </div>
        <div className="stat-card" style={{ '--sc': '#8C8178' }}>
          <div className="stat-label">商品成本</div>
          <div className="stat-value" style={{ color: '#8C8178' }}>¥{yuan(totalCost)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#EF9F27' }}>
          <div className="stat-label">运费</div>
          <div className="stat-value" style={{ color: '#EF9F27' }}>¥{yuan(totalShipping)}</div>
        </div>
        <div className="stat-card" style={{ '--sc': '#13C2C2' }}>
          <div className="stat-label">辅材</div>
          <div className="stat-value" style={{ color: '#13C2C2' }}>¥{yuan(totalPackaging)}</div>
        </div>
      </div>

      <Collapse style={{ marginBottom: 16 }} defaultActiveKey={[]} items={[{
        key: 'more',
        label: <span style={{ fontWeight: 600 }}>更多图表（区域销售占比 · 利润对比 · 成本占比 · 利润排行）——点击展开</span>,
        children: (
          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Card title="区域销售占比（订单数）" size="small" style={{ marginBottom: 16 }}>
                <ReactECharts option={regionPieOption} theme="brand" style={{ height: 320 }} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="订单利润与实际利润对比（扣售后）" size="small" style={{ marginBottom: 16 }}>
                <ReactECharts option={actualProfitOption} theme="brand" style={{ height: 320 }} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="成本与利润占比" size="small" style={{ marginBottom: 16 }}>
                <ReactECharts option={costPieOption} theme="brand" style={{ height: 320 }} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="商品利润排行前十" size="small" style={{ marginBottom: 16 }}>
                <ReactECharts option={profitBarOption} theme="brand" style={{ height: 320 }} />
              </Card>
            </Col>
          </Row>
        ),
      }]} />

      <Card
        title={<span style={{ fontWeight: 600 }}>店铺每日财务明细表</span>}
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <span style={{ color: '#8c8c8c' }}>日期范围：</span>
          <RangePicker value={dailyRange} onChange={r => setDailyRange(r && r[0] && r[1] ? r : null)} allowClear />
        </div>
        {dailyData ? (
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col xs={12} md={4}><Statistic title="总订单" value={dailyData.totals?.order_count} /></Col>
            <Col xs={12} md={4}><Statistic title="总营收" value={yuan(dailyData.totals?.revenue)} prefix="¥" valueStyle={{ color: '#D85A30' }} /></Col>
            <Col xs={12} md={4}><Statistic title="订单利润" value={yuan(dailyData.totals?.profit)} prefix="¥" valueStyle={{ color: '#CF1322' }} /></Col>
            <Col xs={12} md={4}><Statistic title="推广费" value={yuan(dailyData.totals?.ad_spend)} prefix="¥" valueStyle={{ color: '#EF9F27' }} /></Col>
            <Col xs={12} md={4}><Statistic title="实际利润" value={yuan(dailyData.totals?.actual_profit)} prefix="¥" valueStyle={{ color: '#D4537E', fontWeight: 700 }} /></Col>
            <Col xs={12} md={4}><Statistic title="售后影响" value={yuan(dailyData.totals?.after_sale_impact)} prefix="-" valueStyle={{ color: '#EF9F27' }} /></Col>
          </Row>
        ) : null}
        <Table
          columns={dailyColumns()}
          dataSource={(dailyData?.daily || []).map((d, i) => ({ key: i, ...d }))}
          rowKey="key"
          size="small"
          scroll={{ x: 1050, y: 540 }}
          pagination={{ pageSize: 15, showTotal: t => `共 ${t} 天` }}
        />
      </Card>

      <Card
        title={<span style={{ fontWeight: 600 }}>店铺每日财务明细（切换店铺查看单店每日明细）</span>}
        extra={
          <Space>
            <Select style={{ width: 200 }} value={detailShop || undefined} placeholder="选择店铺"
              onChange={v => setDetailShop(v)} options={shops.map(s => ({ value: s, label: s }))} />
            <Upload accept=".xls,.xlsx" showUploadList={false} beforeUpload={handleAdImport}>
              <Button icon={<UploadOutlined />} loading={importingAd}>导入推广费</Button>
            </Upload>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <span style={{ color: '#8c8c8c' }}>日期范围：</span>
          <RangePicker value={detailRange} onChange={r => setDetailRange(r && r[0] && r[1] ? r : null)} allowClear />
        </div>
        {detailData ? (
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col xs={12} md={4}><Statistic title="总订单" value={detailData.totals?.order_count} /></Col>
            <Col xs={12} md={4}><Statistic title="总营收" value={yuan(detailData.totals?.revenue)} prefix="¥" valueStyle={{ color: '#D85A30' }} /></Col>
            <Col xs={12} md={4}><Statistic title="订单利润" value={yuan(detailData.totals?.profit)} prefix="¥" valueStyle={{ color: '#CF1322' }} /></Col>
            <Col xs={12} md={4}><Statistic title="推广费" value={yuan(detailData.totals?.ad_spend)} prefix="¥" valueStyle={{ color: '#EF9F27' }} /></Col>
            <Col xs={12} md={4}><Statistic title="实际利润" value={yuan(detailData.totals?.actual_profit)} prefix="¥" valueStyle={{ color: '#D4537E', fontWeight: 700 }} /></Col>
            <Col xs={12} md={4}><Statistic title="售后影响" value={yuan(detailData.totals?.after_sale_impact)} prefix="-" valueStyle={{ color: '#EF9F27' }} /></Col>
          </Row>
        ) : null}
        <Table
          columns={dailyColumns(detailShop)}
          dataSource={(detailData?.daily || []).map((d, i) => ({ key: i, ...d }))}
          rowKey="key"
          size="small"
          scroll={{ x: 1050, y: 540 }}
          pagination={{ pageSize: 15, showTotal: t => `共 ${t} 天` }}
        />
      </Card>

      <Table
        title={() => <span style={{ fontWeight: 600 }}>商品财务明细（当前筛选范围）</span>}
        columns={productColumns}
        dataSource={productFinance}
        rowKey="spec_code"
        size="small"
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20, showTotal: t => `共 ${t} 个商品` }}
      />

      {/* 全部店铺视图：录入某日各店推广费（列所有店铺，每店一个金额） */}
      <Modal title={`录入推广费（${adModal?.date || ''}）`} open={!!adModal} onOk={saveAdModal} onCancel={() => setAdModal(null)} okText="保存" cancelText="取消" width={460}>
        <div style={{ marginTop: 8 }}>
          {shops.map(s => (
            <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: 13 }}>{s}</span>
              <InputNumber style={{ width: 150 }} min={0} precision={2} placeholder="留空=删除"
                value={adModal?.values?.[s] != null ? adModal.values[s] : undefined}
                onChange={v => setAdModal(m => ({ ...m, values: { ...(m?.values || {}), [s]: v } }))} />
            </div>
          ))}
        </div>
        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8 }}>每个店铺一行，填写金额即可（留空 = 删除该店该日推广费）；保存后实际利润按「订单利润 − 推广费」重算。</div>
      </Modal>

      {/* 产品级推广费录入（按 日期+店铺+产品） */}
      <Modal title={`录入产品推广费`} open={!!prodAdModal} onOk={saveProdAd} onCancel={() => setProdAdModal(null)} okText="保存" cancelText="取消" width={400}>
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600 }}>{prodAdModal?.product_name || ''}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{prodAdModal?.spec_code || ''} · 店铺：{shop === 'all' ? '全部' : shop}</div>
          </div>
          <div style={{ marginBottom: 8, fontSize: 13 }}>日期</div>
          <DatePicker style={{ width: '100%', marginBottom: 12 }}
            value={prodAdModal?.date ? dayjs(prodAdModal.date) : null}
            onChange={v => setProdAdModal(m => ({ ...m, date: v ? v.format('YYYY-MM-DD') : '' }))} />
          <div style={{ marginBottom: 8, fontSize: 13 }}>推广费金额（元）</div>
          <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="留空=删除"
            value={prodAdModal?.amount}
            onChange={v => setProdAdModal(m => ({ ...m, amount: v }))} />
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8 }}>按「日期+店铺+产品」记录该产品当日推广费；付费占比 = 产品推广费 ÷ 产品营收。</div>
        </div>
      </Modal>

      {/* 导出报表：选择月份 */}
      <Modal title="导出财务报表" open={exportModal} onOk={doExport} onCancel={() => setExportModal(false)}
        okText="导出" cancelText="取消" confirmLoading={exporting} width={400}>
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 10, fontSize: 13 }}>选择月份（导出该月整月明细，含订单 / 推广 / 售后）</div>
          <Space wrap>
            <Select style={{ width: 120 }} placeholder="年份" value={exportYear || undefined}
              onChange={v => setExportYear(v)} options={yearOptions} />
            <Select style={{ width: 140 }} placeholder="全部月份" value={exportMonth || undefined}
              onChange={v => setExportMonth(v)}
              options={[{ value: '', label: '全部月份' }, ...MONTH_OPTIONS]} />
          </Space>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 10 }}>
            选具体月份 = 导出该月整月数据；选「全部月份」= 导出所有历史数据。报表含 7 个工作表：发货订单明细、商品成本、区域运费汇总（含整月汇总）、推广费、产品推广费、技术服务费、售后明细。
          </div>
        </div>
      </Modal>
    </div>
  );
}
